/* eslint-disable global-require */
import React from 'react';
import _ from 'lodash';
import CondolenceItem from './CondolenceItem';
import styles from './css/CondolenceCollection.styl';
import {
  bindMethods,
  setRef,
} from '../../../../shared/utilities/componentHelpers';
import {allowPrintedGuestBookAd} from '../../../../shared/utilities/personPageType';
import {AD_PGB} from '../../../../shared/utilities/constants';

/**
 * Magic numbers!
 * @type {Number}
 */
const WINDOW_WIDTH = 1024;
const RESIZE_TIMEOUT = 100;

/**
 * The group of condolences displayed as a guestbook on person page.
 * @class  CondolenceCollection
 */
export default class CondolenceCollection extends React.Component {
  static propTypes = {
    adService: React.PropTypes.object.isRequired,
    data: React.PropTypes.array,
    getAdditionalPayload: React.PropTypes.func,
    handleHubItemClick: React.PropTypes.func,
    hasNextPage: React.PropTypes.bool,
    isFetchingData: React.PropTypes.bool,
    isPhone: React.PropTypes.bool,
    personPageType: React.PropTypes.string,
    primaryFuneralHome: React.PropTypes.object,
    showFlowersAndGifts: React.PropTypes.bool,
    sourceId: React.PropTypes.number,
  };

  /**
   * CondolenceCollection Component constructor
   * @param  {Object} props   Passed in properties.
   * @return {Undefined}           Returns nothing.
   */
  constructor(props) {
    super(props);

    const gridSize = this.getCurrentGridSize(WINDOW_WIDTH);

    this.state = {
      data: this.mutateCondolences(props.data),
    };

    this.fetchingData = false;
    this.gridColumns = gridSize;
    this.heights = [];
    this.imagesLoaded = null;
    this.renderedHubItems = [];

    this.containerRef = null;
    this.setContainerRef = _.partial(setRef, _, 'containerRef', this);

    bindMethods(this, [
      'addRenderedHubItem',
      'handleDidReceiveMessage',
      'handleResize',
      'handleScroll',
    ]);

    this.handleResize = _.debounce(this.handleResize, RESIZE_TIMEOUT);
  }

  /**
   * If the window object exists, add the event listeners and call handleResize().
   * @return {Undefined} Returns nothing
   */
  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('message', this.handleDidReceiveMessage);
    setTimeout(this.handleResize, RESIZE_TIMEOUT);
    this.imagesLoaded = require('imagesloaded');
    this.imagesLoaded(this.containerRef, () => {
      this.organizeHubItems(this.renderedHubItems, this.gridColumns);
    });
  }

  /**
   * Lifecycle Method
   * @param  {Object} newProps New props object
   * @return {Void} Undefined
   */
  componentWillReceiveProps(newProps) {
    if (this.props.data !== newProps.data) {
      this.fetchingData = false;
      this.setState({data: this.mutateCondolences(newProps.data)});
    }
  }

  /**
   * Lifecycle Method
   * @param  {Object} newProps The new properties
   * @return {Boolean} Whether or not to render the component
   */
  shouldComponentUpdate(newProps) {
    return this.props.data !== newProps.data;
  }

  /**
   * Lifecycle Method
   * @return {Undefined} Returns nothing
   */
  componentDidUpdate() {
    /* calling this to avoid jankiness */
    this.organizeHubItems(this.renderedHubItems, this.gridColumns);

    this.imagesLoaded(this.containerRef, () => {
      this.organizeHubItems(this.renderedHubItems, this.gridColumns);
    });
  }

  /**
   * Remove event listeners.
   * @return {Undefined} Returns nothing
   */
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('message', this.handleDidReceiveMessage);
  }

  /**
   * Handles either setting the columns or updating the layout
   * @return {Undefined} Retuns nothing
   */
  handleResize() {
    this.gridColumns = this.getCurrentGridSize(window.innerWidth);

    this.organizeHubItems(this.renderedHubItems, this.gridColumns);
  }

  /**
   * Handler for the scroll event
   * @return {Undefined} Returns nothing
   */
  handleScroll() {
    if (this.props.hasNextPage && !this.props.isFetchingData) {
      const smallestColHeight = this.heights[this.getHeightKey(this.heights)];
      const windowHeight = window.innerHeight;
      const containerRectTop = this.containerRef.getBoundingClientRect().top;
      const diff = containerRectTop + smallestColHeight;

      if (windowHeight > diff) {
        this.fetchingData = true;
        this.props.getAdditionalPayload();
      }
    }
  }

  /**
   * Handler for message event (from ads)
   * @param  {event} e message event
   * @return {Undefined}   returns nothing
   */
  handleDidReceiveMessage(e) {
    try {
      const message = JSON.parse(e.data);
      const action = _.get(message, 'action');
      const frameId = _.get(message, 'frameId');

      if (action === 'resize' && frameId === AD_PGB.positionId) {
        this.organizeHubItems(this.renderedHubItems, this.gridColumns);
      }
    } catch (ex) {
      // do nothing
    }
  }

  /**
   * Ref callback to add the hub items into a stored array for easy dom manipulation
   * @param {Element} hubItem The dom container for the hub item
   * @returns {Undefined} Returns nothing
   */
  addRenderedHubItem(hubItem) {
    if (_.get(this, 'renderedHubItems')) {
      const foundItem = _.find(this.renderedHubItems, item => item === hubItem);

      if (!foundItem) {
        this.renderedHubItems.push(hubItem);
      }
    }
  }

  /**
   * Determines the grid size (1, 2 or 3) based on the window width
   * @param  {Number} width Inner window width.
   * @return {Number}       Returns the number columns the grid should have.
   */
  getCurrentGridSize(width) {
    const LOWER_WINDOW_WIDTH = 600;

    if (width < LOWER_WINDOW_WIDTH) {
      return 1;
    }
    return 2;
  }

  /**
   * Gets the height key for the smallest column
   * @param  {Array} heights The heights array
   * @return {Number} The smallest height key
   */
  getHeightKey(heights) {
    let heightKey = 0;
    let smallestHeight = heights[0];

    _.each(heights, (height, key) => {
      if (height < smallestHeight) {
        smallestHeight = height;
        heightKey = key;
      }
    });

    return heightKey;
  }

  /**
   * Gets the largest column height
   * @param  {Array} heights The heights array
   * @return {Undefined} Returns nothing
   */
  getLargestHeight(heights) {
    let largestHeight = heights[0];

    _.each(heights, height => {
      if (height > largestHeight) {
        largestHeight = height;
      }
    });

    return largestHeight;
  }

  /**
   * Organizes the hub items by setting their positions within the container based on their heights
   * @param  {Array} hubItems The currently rendered hub items
   * @param  {Number} gridSize The current size of the grid
   * @return {Undefined} Returns nothing
   */
  organizeHubItems(hubItems, gridSize) {
    const PERCENT_MULTIPLIER = 100;
    const hubItemPercent = (1 / gridSize) * PERCENT_MULTIPLIER;
    const filteredHubItems = _.compact(hubItems);

    this.heights = [];
    for (let i = 0; i < gridSize; i++) {
      this.heights.push(0);
    }

    _.each(filteredHubItems, hubItem => {
      const heightKey = this.getHeightKey(this.heights);

      hubItem.style.position = 'absolute';
      hubItem.style.width = `${hubItemPercent}%`;
      hubItem.style.top = `${this.heights[heightKey]}px`;
      hubItem.style.left = `${heightKey * hubItemPercent}%`;
      this.heights[heightKey] += hubItem.clientHeight;
    });
    if (_.get(this, 'containerRef')) {
      this.containerRef.style.height = `${this.getLargestHeight(
        this.heights
      )}px`;
    }
  }

  /**
   * Process data to add original index
   * @param {Array} data The data that will be processed
   * @return {Array} Returns and array with the data for the hub teasers.
   */
  mutateCondolences(data) {
    const dataClone = _.map(data, _.clone);
    const adSlot = 3;
    let dataItem = null;
    const processedData = [];

    if (
      data.length > adSlot + 1 &&
      allowPrintedGuestBookAd(this.props.personPageType) &&
      this.props.showFlowersAndGifts
    ) {
      const adObj = {
        isAd: true,
        node: {
          audioUrl: null,
          city: null,
          date: null,
          iconUrl: null,
          id: null,
          message: null,
          name: {firstName: null, lastName: null},
          photo: {url: null, width: 0, height: 0, caption: null},
          relationship: null,
          state: {code: null, name: null},
          video: {id: null},
        },
      };

      dataClone.splice(adSlot, 0, adObj);
    }

    _.each(dataClone, (item, key) => {
      dataItem = _.cloneDeep(item);
      dataItem.originalIndex = key;
      processedData.push(dataItem);
    });

    return processedData;
  }

  /**
   * React Component render.
   * @return {JSX} CondolenceCollection rendered.
   */
  render() {
    return (
      <div
        className={`${styles.CondolenceCollection}${
          this.props.isPhone ? ` ${styles.phone}` : ''
        }`}
        ref={this.setContainerRef}
      >
        <div className={styles.gridGroup}>
          {this.state.data.map((item, key) => (
            <div
              className={styles.groupItem}
              key={key}
              ref={this.addRenderedHubItem}
            >
              <CondolenceItem
                adService={this.props.adService}
                audioUrl={item.node.audioUrl}
                city={item.node.city}
                condolenceId={item.node.condolenceId}
                date={item.node.date}
                firstName={item.node.name.firstName}
                handleClick={_.partial(
                  this.props.handleHubItemClick,
                  item.node.id
                )}
                iconUrl={item.node.iconUrl}
                id={_.get(item, 'originalIndex', '').toString()}
                isAd={item.isAd}
                isModal={false}
                isPhone={this.props.isPhone}
                lastName={item.node.name.lastName}
                likesCount={item.node.likes}
                message={item.node.message}
                photo={item.node.photo}
                primaryFuneralHome={this.props.primaryFuneralHome}
                relationship={item.node.relationship}
                sourceId={this.props.sourceId}
                state={item.node.state}
                video={item.node.video}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/reactModules/pages/Person/components/GuestBook/CondolenceCollection.js